import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchFinanceStats,
  fetchEscrows,
  fetchSettlements,
  type FinanceStats,
  type Escrow,
  type Settlement,
} from '../api/finance';
import { useStakingPool, useSettlementTreasury, useGovernanceInfo, formatAGU } from '../sui';

const DEFAULT_STATS: FinanceStats = {
  totalEscrows: 124,
  activeSettlements: 18,
  openDisputes: 3,
  totalVolume: '2,450,000',
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<FinanceStats>(DEFAULT_STATS);
  const [recent, setRecent] = useState<(Escrow | Settlement)[]>([]);
  const [loading, setLoading] = useState(true);

  // On-chain data hooks
  const { info: stakingInfo } = useStakingPool();
  const { info: treasuryInfo } = useSettlementTreasury();
  const { info: govInfo } = useGovernanceInfo();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, escrows, settlements] = await Promise.all([
          fetchFinanceStats(),
          fetchEscrows(),
          fetchSettlements(),
        ]);
        if (cancelled) return;
        setStats(s);
        const combined = [
          ...escrows.map((e) => ({ ...e, _type: 'escrow' as const })),
          ...settlements.map((s) => ({ ...s, _type: 'settlement' as const })),
        ];
        combined.sort(
          (a, b) =>
            new Date(b.createdAt || 0).getTime() -
            new Date(a.createdAt || 0).getTime()
        );
        setRecent(combined.slice(0, 8));
      } catch {
        /* keep defaults */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const KPI = [
    { icon: '🔒', label: 'Total Escrows', value: stats.totalEscrows, cls: '' },
    { icon: '⚡', label: 'Active Settlements', value: stats.activeSettlements, cls: 'positive' },
    { icon: '⚠️', label: 'Open Disputes', value: stats.openDisputes, cls: stats.openDisputes > 0 ? 'negative' : '' },
    { icon: '💰', label: 'Total Volume (AGU)', value: stats.totalVolume, cls: 'positive' },
  ];

  const ACTIONS = [
    { icon: '🔒', label: 'Create Escrow', desc: 'Lock funds for a task', path: '/escrow' },
    { icon: '📊', label: 'Settlements', desc: 'View settlement timeline', path: '/settlements' },
    { icon: '🛡️', label: 'Disputes', desc: 'Manage disputes', path: '/disputes' },
    { icon: '📄', label: 'Documentation', desc: 'Finance API reference', path: 'https://doc.agentumi.xyz' },
  ];

  return (
    <div>
      <header className="finance-header">
        <h1>Finance Dashboard</h1>
        <p>AI Agent revenue management and DeFi integration platform</p>
      </header>

      {/* ── KPI Grid ── */}
      <div className="finance-grid">
        {KPI.map((k) => (
          <div className="finance-card" key={k.label}>
            <div className="finance-card-header">
              <span className="finance-card-title">
                <span className="finance-icon">{k.icon}</span>
                {k.label}
              </span>
            </div>
            <div className={`finance-stat-value ${k.cls}`}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* ── On-chain Status ── */}
      <div className="finance-card" style={{ marginBottom: 24 }}>
        <div className="finance-card-header">
          <span className="finance-card-title">
            <span className="finance-icon">⛓️</span>
            SUI On-chain Status
          </span>
          <span className="finance-badge info">Live</span>
        </div>
        <div className="finance-grid" style={{ marginTop: 16 }}>
          <div style={{ textAlign: 'center' }}>
            <div className="finance-stat-label">Staking Pool</div>
            <div className="finance-stat-value positive" style={{ fontSize: 20 }}>{formatAGU(stakingInfo.totalStaked)} AGU</div>
            <div className="finance-stat-label">{stakingInfo.stakerCount} stakers • {stakingInfo.rewardRate}% APR</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div className="finance-stat-label">Escrow Locked</div>
            <div className="finance-stat-value positive" style={{ fontSize: 20 }}>{formatAGU(treasuryInfo.totalLocked)} AGU</div>
            <div className="finance-stat-label">{treasuryInfo.escrowCount} active escrows</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div className="finance-stat-label">Total Settled</div>
            <div className="finance-stat-value" style={{ fontSize: 20 }}>{formatAGU(treasuryInfo.totalSettled)} AGU</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div className="finance-stat-label">Governance</div>
            <div className="finance-stat-value" style={{ fontSize: 20 }}>{govInfo.proposalCount} proposals</div>
          </div>
        </div>
      </div>

      {/* ── Recent Activity ── */}
      <div className="finance-card" style={{ marginBottom: 24 }}>
        <div className="finance-card-header">
          <span className="finance-card-title">📋 Recent Activity</span>
          <span className="finance-badge info">{loading ? '…' : `${recent.length} items`}</span>
        </div>
        {loading ? (
          <div className="finance-loading">Loading…</div>
        ) : recent.length === 0 ? (
          <div className="finance-empty">No recent activity. Create an escrow to get started.</div>
        ) : (
          <div className="finance-table-wrap">
            <table className="finance-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Agent / Task</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r: any, i) => (
                  <tr key={i}>
                    <td>
                      <span className="finance-badge info">
                        {r._type === 'escrow' ? '🔒 Escrow' : '⚡ Settlement'}
                      </span>
                    </td>
                    <td className="mono">{r.agentId || r.escrowId || '—'}</td>
                    <td className="finance-stat-value positive" style={{ fontSize: 16, margin: 0 }}>
                      {r.amount || 0} {r.token || 'AGU'}
                    </td>
                    <td>
                      <span
                        className={`finance-badge ${
                          r.status === 'locked' || r.status === 'pending'
                            ? 'warning'
                            : r.status === 'released' || r.status === 'completed'
                            ? 'success'
                            : 'info'
                        }`}
                      >
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Quick Actions ── */}
      <div className="finance-grid">
        {ACTIONS.map((a) => (
          <div
            className="finance-card finance-action-card"
            key={a.label}
            onClick={() =>
              a.path.startsWith('http')
                ? window.open(a.path, '_blank')
                : navigate(a.path)
            }
            style={{ cursor: 'pointer' }}
          >
            <span className="finance-icon" style={{ fontSize: 32 }}>{a.icon}</span>
            <h3 style={{ marginTop: 12 }}>{a.label}</h3>
            <p className="finance-stat-label">{a.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
